import React, { useState } from 'react';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Alert from 'Components/DesignSystem/Alert/Alert';
import Button from 'Components/DesignSystem/Button/Button';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import { Add, Delete } from 'Components/DesignSystem/Iconography/Icons';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import ChipsInput from 'Pages/GMS/Resolve/components/ChipsInput';
import type { DropdownOption } from '../../../../types/DesignSystem/Dropdown';
import { ConfigField as ConfigFieldDef, ConfigValue } from './config/adminConfig.types';
import { fieldDefault } from './config/configRules';

export interface ConfigFieldProps {
  field: ConfigFieldDef;
  value: ConfigValue;
  onChange: (value: ConfigValue) => void;
  disabled?: boolean;
  /** Errors keyed by this field's path (`path`, `path.<child>`, `path.<index>.<key>`). */
  errors?: Record<string, string>;
  path: string;
  testId: string;
  /** Help text that replaces the static one (e.g. from `helpTextWhen`). */
  helpText?: string;
  onBlur?: () => void;
}

const copyToClipboard = (text: string, label: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(`${label} copied to clipboard!`))
    .catch(() => toast.error(`Failed to copy ${label}`));
};

/** Multi-line tooltip text ("\n" in the config) as the DS info popover expects a string. */
const tooltipOf = (field: ConfigFieldDef): string | undefined => (field.tooltip ? field.tooltipText : undefined);

const asRecord = (value: ConfigValue): Record<string, ConfigValue> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, ConfigValue>) : {};

/** Number input: '' stays '' (or null when nullable) so validation can report it. */
const parseNumber = (raw: string, nullable?: boolean): ConfigValue => {
  if (raw.trim() === '') return nullable ? null : '';
  const number = Number(raw);
  return Number.isFinite(number) ? number : raw;
};

/** One setting from the admin config, drawn with the design-system control for its type. */
/** Group children that need a whole row (a chip list or a table), not half of one. */
const FULL_ROW_TYPES = ['stringList', 'keyValueList', 'objectList'];

const ConfigField: React.FC<ConfigFieldProps> = ({ field, value, onChange, disabled, errors = {}, path, testId, helpText, onBlur }) => {
  const error = errors[path];
  const label = field.label ?? '';
  const helper = error || helpText || field.helpText || '';

  switch (field.type) {
    case 'toggle':
      return (
        <div className="tw-flex tw-items-center tw-justify-between">
          <FieldTitle label={label} infoMessage={tooltipOf(field)} />
          <Toggle label="" checked={value === true} onChange={(checked) => onChange(checked)} disabled={disabled} data-testid={testId} />
        </div>
      );

    case 'checkbox':
      return (
        <Checkbox label={label} checked={value === true} onChange={(checked) => onChange(checked)} disabled={disabled} data-testid={testId} />
      );

    case 'text':
    case 'url':
    case 'secret':
      return (
        <TextField
          label={label}
          placeholder={field.placeholder}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(next) => onChange(next)}
          onBlur={onBlur}
          disabled={disabled || field.readOnly}
          required={field.required}
          error={!!error}
          helperText={helper}
          infoMessage={tooltipOf(field)}
          maxLength={field.validation?.maxLength}
          showCopyButton={field.copyable}
          onCopy={field.copyable ? () => copyToClipboard(String(value ?? ''), field.copyLabel ?? label) : undefined}
          data-testid={testId}
        />
      );

    case 'number':
      return (
        <TextField
          label={label}
          type="number"
          placeholder={field.placeholder}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(next) => onChange(parseNumber(next, field.nullable))}
          onBlur={onBlur}
          disabled={disabled}
          required={field.required}
          error={!!error}
          helperText={helper}
          infoMessage={tooltipOf(field)}
          minValue={field.validation?.min}
          maxValue={field.validation?.max}
          decimalAllowed={!field.validation?.integer}
          data-testid={testId}
        />
      );

    case 'select': {
      const empty = field.emptyValues?.length ? [{ value: String(field.emptyValues[field.emptyValues.length - 1]), label: field.placeholder ?? '' }] : [];
      const options = [...empty, ...(field.options ?? []).map((option) => ({ value: String(option.value), label: option.label }))];
      const toValue = (raw: string): ConfigValue => {
        const match = (field.options ?? []).find((option) => String(option.value) === raw);
        return match ? match.value : raw;
      };
      const current = value === undefined || value === null || value === '' ? (empty[0]?.value ?? '') : String(value);
      return (
        <Dropdown
          label={label}
          placeholder={field.placeholder}
          value={current}
          onChange={(next) => onChange(toValue(String(next)))}
          options={options as DropdownOption<string>[]}
          disabled={disabled}
          required={field.required}
          error={!!error}
          helperText={helper}
          infoMessage={tooltipOf(field) ? <span className="tw-whitespace-pre-line">{tooltipOf(field)}</span> : undefined}
          data-testid={testId}
        />
      );
    }

    case 'stringList':
      return (
        <div className="tw-space-y-2">
          <FieldTitle label={label} infoMessage={tooltipOf(field)} />
          <ChipsInput testId={testId} value={Array.isArray(value) ? (value as string[]) : []} onChange={onChange} extendable disabled={disabled} />
          {error && <p className="tw-text-sm tw-text-tone-danger-ink" data-testid={`${testId}-error`}>{error}</p>}
        </div>
      );

    case 'keyValueList':
      return <KeyValueListField field={field} value={asRecord(value)} onChange={onChange} disabled={disabled} error={error} testId={testId} />;

    case 'objectList':
      return (
        <ObjectListField field={field} value={Array.isArray(value) ? (value as Record<string, ConfigValue>[]) : []} onChange={onChange}
          disabled={disabled} errors={errors} path={path} testId={testId} />
      );

    case 'group': {
      const values = asRecord(value);
      return (
        <div className="tw-space-y-2" data-testid={testId}>
          {label && <FieldTitle label={label} infoMessage={tooltipOf(field)} />}
          {/* Two columns, inputs aligned along the bottom so a label that wraps (narrow panel)
              never pushes its own input out of line; a list child takes a full row. */}
          <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 tw-items-end">
            {(field.children ?? []).map((child) => (
              <div className={FULL_ROW_TYPES.includes(child.type) ? 'tw-col-span-2' : 'tw-min-w-0'} key={child.key}
                data-testid={`${testId}-${child.key}-cell`}>
                <ConfigField field={child} value={values[child.key]} onChange={(next) => onChange({ ...values, [child.key]: next })}
                  disabled={disabled} errors={errors} path={`${path}.${child.key}`} testId={`${testId}-${child.key}`} />
              </div>
            ))}
          </div>
          {error && <p className="tw-text-sm tw-text-tone-danger-ink" data-testid={`${testId}-error`}>{error}</p>}
        </div>
      );
    }

    case 'banner':
      return (
        <Alert tone={field.severity ?? 'warning'} data-testid={testId}>
          {field.message}
        </Alert>
      );

    default:
      return null;
  }
};

const KeyValueListField: React.FC<{
  field: ConfigFieldDef;
  value: Record<string, ConfigValue>;
  onChange: (value: ConfigValue) => void;
  disabled?: boolean;
  error?: string;
  testId: string;
}> = ({ field, value, onChange, disabled, error, testId }) => {
  const [draftKey, setDraftKey] = useState('');
  const entries = Object.entries(value);
  const add = () => {
    const key = draftKey.trim();
    if (!key || key in value) return;
    onChange({ ...value, [key]: 0 });
    setDraftKey('');
  };
  return (
    <div className="tw-space-y-2" data-testid={testId}>
      <FieldTitle label={field.label ?? ''} infoMessage={tooltipOf(field)} />
      {entries.map(([key, entryValue]) => (
        <div className="tw-flex tw-gap-2 tw-items-end" key={key} data-testid={`${testId}-row-${key}`}>
          <div className="tw-flex-1 tw-text-sm">{key}</div>
          <div className="tw-w-32">
            <TextField label={field.valueLabel ?? ''} type="number" value={entryValue === null || entryValue === undefined ? '' : String(entryValue)}
              onChange={(next) => onChange({ ...value, [key]: parseNumber(next) })} disabled={disabled} data-testid={`${testId}-value-${key}`} />
          </div>
          <IconButton aria-label={`Remove ${key}`} variant="subtle" disabled={disabled}
            onClick={() => onChange(Object.fromEntries(entries.filter(([k]) => k !== key)))} data-testid={`${testId}-remove-${key}`}>
            <Delete />
          </IconButton>
        </div>
      ))}
      <div className="tw-flex tw-gap-2 tw-items-end">
        <div className="tw-flex-1">
          <TextField label={field.keyLabel ?? ''} value={draftKey} onChange={setDraftKey} disabled={disabled} data-testid={`${testId}-new-key`} />
        </div>
        <Button variant="secondary" onClick={add} disabled={disabled || !draftKey.trim()} data-testid={`${testId}-add`}>Add</Button>
      </div>
      {error && <p className="tw-text-sm tw-text-tone-danger-ink" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
};

const ObjectListField: React.FC<{
  field: ConfigFieldDef;
  value: Record<string, ConfigValue>[];
  onChange: (value: ConfigValue) => void;
  disabled?: boolean;
  errors: Record<string, string>;
  path: string;
  testId: string;
}> = ({ field, value, onChange, disabled, errors, path, testId }) => {
  const itemFields = field.itemFields ?? [];
  const newItem = () => Object.fromEntries(itemFields.map((itemField) => [itemField.key, fieldDefault(itemField)]));
  const update = (index: number, key: string, next: ConfigValue) =>
    onChange(value.map((item, i) => (i === index ? { ...item, [key]: next } : item)));
  return (
    <div className="tw-space-y-3" data-testid={testId}>
      <FieldTitle label={field.label ?? ''} infoMessage={tooltipOf(field)} />
      {value.map((item, index) => {
        const itemTitle = String(item[field.itemLabelField ?? ''] || `${field.itemLabel ?? 'item'} ${index + 1}`);
        return (
          // Items have no stable id of their own; the index is the only key the stored list carries.
          <div key={index} className="tw-space-y-3 tw-p-3 tw-border tw-border-edge tw-rounded" data-testid={`${testId}-item-${index}`}>
            <div className="tw-flex tw-items-center tw-justify-between">
              <span className="tw-text-sm tw-font-medium">{itemTitle}</span>
              <IconButton aria-label={`Remove ${itemTitle}`} variant="subtle" disabled={disabled}
                onClick={() => onChange(value.filter((_, i) => i !== index))} data-testid={`${testId}-item-${index}-remove`}>
                <Delete />
              </IconButton>
            </div>
            {itemFields.map((itemField) => (
              <ConfigField key={itemField.key} field={itemField} value={item[itemField.key]} onChange={(next) => update(index, itemField.key, next)}
                disabled={disabled} errors={errors} path={`${path}.${index}.${itemField.key}`} testId={`${testId}-item-${index}-${itemField.key}`} />
            ))}
          </div>
        );
      })}
      <Button variant="secondary" startIcon={<Add />} onClick={() => onChange([...value, newItem()])} disabled={disabled} data-testid={`${testId}-add`}>
        {`Add ${field.itemLabel ?? 'item'}`}
      </Button>
    </div>
  );
};

export default ConfigField;
